import React, { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Paper, Snackbar, Alert } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { BookList } from '../components/BookList';
import { BookForm } from '../components/BookForm';
import { BookDetail } from '../components/BookDetail';
import { BookImport } from '../components/BookImport';
import { useAppStore } from '../stores/useAppStore';
import { isStudent, isTeacher, canManageBooksFull } from '../utils/rbac';
import type { Book } from '../types';

export const BookManagementPage: React.FC = () => {
  const location = useLocation();
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const isStudentRole = isStudent(userRoles);
  const canManageFull = canManageBooksFull(userRoles);
  // NOTE: If user has both Librarian and Teacher, canManageFull takes priority
  // Only treat as "Teacher-only" if NOT canManageFull
  const isTeacherRole = isTeacher(userRoles) && !canManageFull;

  const [bookFormOpen, setBookFormOpen] = useState(false);
  const [bookDetailOpen, setBookDetailOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [filterFromReports, setFilterFromReports] = useState<string | null>(null);
  const [categoryIdFromCategories, setCategoryIdFromCategories] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle navigation state from Reports
  useEffect(() => {
    if (location.state?.fromReports && location.state?.filter) {
      setFilterFromReports(location.state.filter);
      // Clear the state to prevent re-applying filter on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle navigation state from Categories
  useEffect(() => {
    if (location.state?.fromCategories && location.state?.categoryId) {
      setCategoryIdFromCategories(location.state.categoryId);
      // Clear the state to prevent re-applying filter on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleAddBook = () => {
    setSelectedBook(null);
    setIsEditing(false);
    setBookFormOpen(true);
  };

  const handleEditBook = (book: Book) => {
    setSelectedBook(book);
    setIsEditing(true);
    setBookFormOpen(true);
  };

  const handleViewBook = (book: Book) => {
    setSelectedBook(book);
    setBookDetailOpen(true);
  };

  const handleBookSaved = (book: Book) => {
    const wasEditing = isEditing;
    setBookFormOpen(false);
    setSelectedBook(null);
    setIsEditing(false);
    setRefreshTrigger(prev => prev + 1);
    setSnackbarMessage(wasEditing ? 'Cập nhật sách thành công!' : 'Thêm sách thành công!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleBookSaveError = (error: string) => {
    setSnackbarMessage(error);
    setSnackbarSeverity('error');
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleFormClose = () => {
    setBookFormOpen(false);
    setSelectedBook(null);
    setIsEditing(false);
  };

  const handleDetailClose = () => {
    setBookDetailOpen(false);
    setSelectedBook(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
      {!isStudentRole && !isTeacherRole && (
        <Paper sx={{ mb: 2, width: '100%', boxSizing: 'border-box' }}>
          <Tabs value={activeTab} onChange={handleTabChange} sx={{ width: '100%' }}>
            <Tab label="Danh Sách Sách" />
            {canManageFull && <Tab label="Import Hàng Loạt" />}
          </Tabs>
        </Paper>
      )}

      {(isStudentRole || isTeacherRole) && (
        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
          <BookList
            onAdd={handleAddBook}
            onEdit={handleEditBook}
            onView={handleViewBook}
            filterFromReports={filterFromReports}
            categoryIdFromCategories={categoryIdFromCategories}
            refreshTrigger={refreshTrigger}
          />
        </Box>
      )}

      {!isStudentRole && !isTeacherRole && (
        <Box sx={{ width: '100%', boxSizing: 'border-box' }}>
          <Box
            sx={{
              display: activeTab === 0 ? 'block' : 'none',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            <BookList
              onAdd={handleAddBook}
              onEdit={handleEditBook}
              onView={handleViewBook}
              filterFromReports={filterFromReports}
              categoryIdFromCategories={categoryIdFromCategories}
              refreshTrigger={refreshTrigger}
            />
          </Box>

          {canManageFull && (
            <Box
              sx={{
                display: activeTab === 1 ? 'block' : 'none',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <BookImport
                onViewList={() => {
                  setActiveTab(0);
                  // Trigger refresh when switching back to list view manually
                  setRefreshTrigger(prev => prev + 1);
                }}
                onImportSuccess={() => {
                  // Trigger refresh upon successful import
                  setRefreshTrigger(prev => prev + 1);
                }}
              />
            </Box>
          )}
        </Box>
      )}

      <BookForm
        open={bookFormOpen}
        onClose={handleFormClose}
        onSave={handleBookSaved}
        onError={handleBookSaveError}
        book={selectedBook}
        title={isEditing ? 'Chỉnh sửa sách' : 'Thêm sách mới'}
      />

      <BookDetail
        open={bookDetailOpen}
        onClose={handleDetailClose}
        book={selectedBook}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
